if (!customElements.get('slider-component')) {
    class SliderComponent extends HTMLElement {
        constructor() {
            super();
            this.slider = this.querySelector('[data-slider]');
            this.prevBtn = this.querySelector('[data-action="prev"]');
            this.nextBtn = this.querySelector('[data-action="next"]');
            this.controls = this.querySelector('[data-slider-controls]');

            if (!this.slider) return;

            this.init();
        }

        init() {
            this.dots = this.querySelectorAll('[data-pagination-dot]');
            this.isInfinite = this.dataset.infinite === 'true';
            this.autoplay = this.dataset.autoplay === 'true';
            this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed) || 4000;

            if (this.isInfinite) {
                this.setupInfinite();
            }

            if (this.autoplay) {
                this.startAutoplay();
                this.addEventListener('mouseenter', this.stopAutoplay.bind(this));
                this.addEventListener('mouseleave', this.startAutoplay.bind(this));
                this.addEventListener('focusin', this.stopAutoplay.bind(this));
                this.addEventListener('focusout', this.startAutoplay.bind(this));
                this.addEventListener('click', this.startAutoplay.bind(this));
                this.addEventListener('touchstart', this.startAutoplay.bind(this), { passive: true });
            }

            this.updateScrollState();

            this.slider.addEventListener('scroll', () => {
                this.updateScrollState();

                if (this.isInfinite) {
                    clearTimeout(this.scrollTimeout);
                    this.scrollTimeout = setTimeout(() => {
                        this.handleInfiniteScroll();
                    }, 50); // Small delay to let smooth scroll settle
                }
            });

            if (this.prevBtn) {
                this.prevBtn.addEventListener('click', this.scrollPrev.bind(this));
            }

            if (this.nextBtn) {
                this.nextBtn.addEventListener('click', this.scrollNext.bind(this));
            }

            if (this.dots.length > 0) {
                this.dots.forEach((dot, index) => {
                    dot.addEventListener('click', (event) => {
                        event.preventDefault();
                        this.scrollToSlide(index);
                    });
                });
            }

            const resizeObserver = new ResizeObserver(() => {
                if (this.isInfinite) {
                    this.handleInfiniteScroll();
                }
                this.updateScrollState();
            });
            resizeObserver.observe(this.slider);
        }

        startAutoplay() {
            if (!this.autoplay) return;
            this.stopAutoplay();
            this.autoplayTimer = setInterval(() => {
                this.scrollNext();
            }, this.autoplaySpeed);
        }

        stopAutoplay() {
            if (this.autoplayTimer) clearInterval(this.autoplayTimer);
        }

        setupInfinite() {
            const children = Array.from(this.slider.children);
            this.originalCount = children.length;
            if (this.originalCount === 0) return;

            // Clone items for the tail end
            children.forEach(child => {
                const clone = child.cloneNode(true);
                clone.setAttribute('aria-hidden', 'true');
                this.slider.appendChild(clone);
            });

            // Clone items for the head end
            const prependClones = children.map(c => c.cloneNode(true));
            prependClones.reverse().forEach(child => {
                child.setAttribute('aria-hidden', 'true');
                this.slider.insertBefore(child, this.slider.firstChild);
            });

            // Jump to the first original child without flashing
            requestAnimationFrame(() => {
                const firstOrig = this.slider.children[this.originalCount];
                if (firstOrig) {
                    this.slider.style.scrollBehavior = 'auto';
                    this.slider.style.scrollSnapType = 'none';
                    this.slider.scrollTo({ left: firstOrig.offsetLeft, behavior: 'auto' });
                    this.slider.offsetHeight;
                    this.slider.style.scrollBehavior = '';
                    this.slider.style.scrollSnapType = '';
                }
            });
        }

        handleInfiniteScroll() {
            if (!this.originalCount) return;

            const firstOrig = this.slider.children[this.originalCount];
            const firstTail = this.slider.children[this.originalCount * 2];

            if (!firstOrig || !firstTail) return;

            const scrollLeft = this.slider.scrollLeft;
            const trackWidth = firstTail.offsetLeft - firstOrig.offsetLeft;

            if (scrollLeft >= firstTail.offsetLeft - 5) { 
                this.slider.style.scrollBehavior = 'auto'; 
                this.slider.style.scrollSnapType = 'none';
                this.slider.scrollTo({ left: scrollLeft - trackWidth, behavior: 'auto' });
                this.slider.offsetHeight; // Force reflow securely
                this.slider.style.scrollBehavior = '';
                this.slider.style.scrollSnapType = '';
            }
            else if (scrollLeft < firstOrig.offsetLeft - 5) {
                this.slider.style.scrollBehavior = 'auto';
                this.slider.style.scrollSnapType = 'none';
                this.slider.scrollTo({ left: scrollLeft + trackWidth, behavior: 'auto' });
                this.slider.offsetHeight;
                this.slider.style.scrollBehavior = '';
                this.slider.style.scrollSnapType = '';
            }
        }

        updateScrollState() {
            if (!this.slider) return;

            if (this.isInfinite) {
                this.prevBtn?.classList.remove('opacity-50');
                this.nextBtn?.classList.remove('opacity-50');
                if (this.controls) this.controls.classList.remove('d-none');
            } else {
                const { scrollLeft, scrollWidth, clientWidth } = this.slider;

                if (scrollWidth <= clientWidth + 2) {
                    if (this.controls) this.controls.classList.add('d-none');
                    this.prevBtn?.classList.add('opacity-50');
                    this.nextBtn?.classList.add('opacity-50');
                    return;
                } else {
                    if (this.controls) this.controls.classList.remove('d-none');
                }

                const atStart = scrollLeft <= 2;
                const atEnd = (scrollLeft + clientWidth) >= (scrollWidth - 2);

                if (this.prevBtn) {
                    if (atStart) this.prevBtn.classList.add('opacity-50');
                    else this.prevBtn.classList.remove('opacity-50');
                }

                if (this.nextBtn) {
                    if (atEnd) this.nextBtn.classList.add('opacity-50');
                    else this.nextBtn.classList.remove('opacity-50');
                }
            }

            if (this.dots.length > 0) {
                const clientWidth = this.slider.clientWidth;
                const scrollLeft = this.slider.scrollLeft;

                let activeIndex = 0;
                let minDistance = Infinity;

                Array.from(this.slider.children).forEach((child, index) => {
                    const childCenter = child.offsetLeft + (child.offsetWidth / 2);
                    const relativeSliderCenter = scrollLeft + (clientWidth / 2);
                    const dist = Math.abs(childCenter - relativeSliderCenter);

                    if (dist < minDistance) {
                        minDistance = dist;
                        activeIndex = index;
                    }
                });

                if (this.isInfinite && this.originalCount) {
                    activeIndex = activeIndex % this.originalCount;
                }

                const activeClassStr = this.dataset.dotActiveClass || 'bg-dark';
                const inactiveClassStr = this.dataset.dotInactiveClass || 'bg-secondary';
                const activeClasses = activeClassStr.split(' ');
                const inactiveClasses = inactiveClassStr.split(' ');

                this.dots.forEach((dot, index) => {
                    if (index === activeIndex) {
                        dot.classList.remove(...inactiveClasses);
                        dot.classList.add(...activeClasses);
                    } else {
                        dot.classList.remove(...activeClasses);
                        dot.classList.add(...inactiveClasses);
                    }
                });
            }
        }

        scrollToSlide(index) {
            let actualIndex = index;
            if (this.isInfinite && this.originalCount) {
                actualIndex = index + this.originalCount;
            }

            const slide = this.slider.children[actualIndex];
            if (slide) {
                const slideLeft = slide.offsetLeft;
                this.slider.scrollTo({
                    left: slideLeft,
                    behavior: 'smooth'
                });
            }
        }

        getItemWidth() {
            const items = Array.from(this.slider.children);
            for (let item of items) {
                if (item.classList.contains('d-none') || item.offsetWidth === 0) continue;
                return item.offsetWidth;
            }
            return this.slider.clientWidth;
        }

        scrollNext() {
            if (this.isInfinite) this.handleInfiniteScroll();
            const step = this.getItemWidth();
            this.slider.scrollBy({ left: step, behavior: 'smooth' });
        }

        scrollPrev() {
            if (this.isInfinite) this.handleInfiniteScroll();
            const step = this.getItemWidth();
            this.slider.scrollBy({ left: -step, behavior: 'smooth' });
        }
    }

    customElements.define('slider-component', SliderComponent);
}
